/**
 * The recurring surfaces of the Brand Presence screens as Tailwind class lists, so a card, a toolbar
 * button or a menu looks the same on every screen and changes in one place.
 *
 * Each size is its own list rather than a base list plus an override: two utilities for the same
 * property on one element resolve by their order in the stylesheet, not by their order in the list.
 */

/**
 * The class that brings the Brand Presence utilities to what renders outside the page container: a
 * modal or a menu opened in a portal on the document body.
 */
export const BP_PORTAL_CLASS_NAME = 'brand-presence-portal';

/**
 * The class that brings the Brand Presence utilities to a screen a CMS frames on its own, outside the
 * App layout and its page container: the Optimize tab, the Content health box beside the editor. The
 * utilities apply to what is inside an element carrying it, so the screen is drawn from the same
 * recipes as the App's screens rather than from look-alikes.
 */
export const BP_CMS_SCREEN_CLASS_NAME = 'brand-presence-cms-screen';

/** What a control waiting on its own write looks like; it keeps its place in the tab order all the while. */
export const BP_WAITING_CONTROL_CLASSES = 'cursor-progress opacity-60';

/** The name of a figure above its value, in the typewriter face: the title of a figure card. */
export const BP_FIGURE_TITLE_CLASSES = 'font-eyebrow text-[0.6875rem] font-medium uppercase tracking-[0.14em] text-slate-500';

/** A small text button in the primary colour, tinting on hover, with a visible focus ring: "Show all", "+3 more". */
export const BP_TEXT_BUTTON_SMALL_CLASSES =
  'rounded-md px-1.5 py-0.5 text-xs font-semibold text-primary transition-colors hover:bg-primary/[0.07] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40';

/**
 * A tag as a small pill with a hairline ring; a long name wraps inside the pill, a name with no place to
 * break included, so a pill in a row of them is never wider than the row.
 */
export const BP_TAG_CHIP_CLASSES =
  'inline-flex max-w-full items-center rounded-xl bg-slate-100 px-2.5 py-0.5 text-[0.6875rem] font-semibold leading-5 text-slate-700 ring-1 ring-inset ring-slate-200/70 [overflow-wrap:anywhere]';

/** A white card lifted off the page by a hairline ring and a soft shadow. */
export const BP_CARD_CLASSES = 'rounded-2xl bg-white shadow-card ring-1 ring-slate-200/70';

const TOOLBAR_BUTTON_SHAPE =
  'inline-flex items-center gap-2 rounded-lg border text-sm font-semibold transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:cursor-not-allowed disabled:opacity-50';

const TOOLBAR_BUTTON_LOOK = `${TOOLBAR_BUTTON_SHAPE} border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900`;

const PRIMARY_BUTTON_LOOK =
  'inline-flex items-center justify-center gap-2 rounded-lg bg-primary text-sm font-semibold text-white shadow-sm transition-all duration-150 hover:brightness-110 hover:shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none';

/** A quiet button in a toolbar: outlined, darkening on hover, with a visible focus ring. */
export const BP_TOOLBAR_BUTTON_CLASSES = `${TOOLBAR_BUTTON_LOOK} h-9 px-3`;

/** The toolbar button while the menu it opens is open. */
export const BP_TOOLBAR_BUTTON_OPEN_CLASSES = `${TOOLBAR_BUTTON_SHAPE} border-primary/40 bg-primary/[0.06] text-primary h-9 px-3`;

/** The quiet button at the size used inside a menu. */
export const BP_TOOLBAR_BUTTON_SMALL_CLASSES = `${TOOLBAR_BUTTON_LOOK} h-8 px-3`;

/** The one action a group of controls leads to, filled in the primary colour. */
export const BP_PRIMARY_BUTTON_CLASSES = `${PRIMARY_BUTTON_LOOK} h-9 px-4`;

/** The primary button at the size used inside a menu. */
export const BP_PRIMARY_BUTTON_SMALL_CLASSES = `${PRIMARY_BUTTON_LOOK} h-8 px-3.5`;

/** A menu or popover opened from a toolbar control. */
export const BP_POPOVER_CLASSES = 'rounded-xl bg-white p-3 shadow-card-hover ring-1 ring-slate-200/80 motion-safe:animate-bp-slide-down';

/** A small heading inside a menu that groups the options under it. */
export const BP_POPOVER_GROUP_LABEL_CLASSES = 'font-eyebrow text-[0.625rem] font-medium uppercase tracking-[0.14em] text-slate-500';

const TABLE_HEAD_CELL_LOOK =
  'py-3 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-slate-500 whitespace-nowrap border-b border-slate-200/70';

/** A table header cell. */
export const BP_TABLE_HEAD_CELL_CLASSES = `${TABLE_HEAD_CELL_LOOK} px-4`;

/** The surface an AI answer is read on: a white card with a hairline ring, padded like prose. */
export const BP_ANSWER_SURFACE_CLASSES = 'rounded-2xl bg-white px-6 py-5 shadow-card ring-1 ring-slate-200/70';

/**
 * The typography of an AI answer's own markdown, whichever way it is rendered.
 *
 * A calm heading scale — the answer's own headings sit well below the page title — a comfortable
 * line length for the text, list and quote spacing, links in the page's primary colour, and tables
 * with a light header and hairline row separators instead of a full grid.
 */
export const BP_ANSWER_PROSE_CLASSES = [
  'text-[0.9375rem] leading-[1.7] text-slate-700 marker:text-slate-400 [&>:first-child]:mt-0 [&>:last-child]:mb-0',
  '[&_h1]:mb-2 [&_h1]:mt-6 [&_h1]:text-lg [&_h1]:font-semibold [&_h1]:leading-snug [&_h1]:tracking-tight [&_h1]:text-slate-900',
  '[&_h2]:mb-2 [&_h2]:mt-6 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:leading-snug [&_h2]:tracking-tight [&_h2]:text-slate-900',
  '[&_h3]:mb-1.5 [&_h3]:mt-5 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:leading-snug [&_h3]:text-slate-900',
  '[&_h4]:mb-1 [&_h4]:mt-4 [&_h4]:text-[0.9375rem] [&_h4]:font-semibold [&_h4]:text-slate-900',
  '[&_h5]:mb-1 [&_h5]:mt-4 [&_h5]:text-sm [&_h5]:font-semibold [&_h5]:text-slate-800',
  '[&_h6]:mb-1 [&_h6]:mt-4 [&_h6]:text-sm [&_h6]:font-semibold [&_h6]:text-slate-600',
  '[&_p]:mb-4 [&_p]:mt-0 [&_p]:text-[0.9375rem] [&_p]:leading-[1.7] [&_p]:max-w-[76ch] [&_li]:max-w-[76ch] [&_blockquote]:max-w-[76ch]',
  '[&_ul]:mb-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:mb-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-1.5 [&_li>p]:mb-1.5',
  '[&_li_ul]:mb-0 [&_li_ul]:mt-1.5 [&_li_ol]:mb-0 [&_li_ol]:mt-1.5',
  '[&_blockquote]:my-4 [&_blockquote]:border-l-2 [&_blockquote]:border-slate-200 [&_blockquote]:pl-4 [&_blockquote]:text-slate-600',
  '[&_a]:font-medium [&_a]:text-primary [&_a]:underline [&_a]:decoration-primary/30 [&_a]:underline-offset-2 [&_a:hover]:decoration-primary',
  '[&_strong]:font-semibold [&_strong]:text-slate-900',
  '[&_code]:rounded [&_code]:bg-slate-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[0.85em]',
  '[&_pre]:mb-4 [&_pre]:rounded-lg [&_pre]:bg-slate-50 [&_pre]:p-3 [&_pre]:text-[0.8125rem] [&_pre]:ring-1 [&_pre]:ring-slate-200/70 [&_pre_code]:bg-transparent [&_pre_code]:p-0',
  '[&_hr]:my-6 [&_hr]:border-slate-200',
  '[&_table]:mb-4 [&_table]:w-full [&_table]:border-collapse [&_table]:text-sm [&_table]:leading-6',
  '[&_th]:border-b [&_th]:border-slate-200 [&_th]:bg-slate-50 [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:text-xs [&_th]:font-semibold [&_th]:text-slate-600',
  '[&_td]:border-b [&_td]:border-slate-100 [&_td]:px-3 [&_td]:py-2 [&_td]:align-top [&_tbody_tr:last-child>td]:border-b-0',
  '[&_img]:h-auto [&_img]:max-w-[300px]',
].join(' ');

/** A table header cell of a table whose cells are padded wider, as inside a tabbed card. */
export const BP_TABLE_HEAD_CELL_WIDE_CLASSES = `${TABLE_HEAD_CELL_LOOK} px-6`;
